import { useEffect, useMemo, useRef } from "react";
import { get, set } from "firebase/database";
import { recentlyLeft, roomRef, roomUpdate, serverNow, systemMsg } from "./room";
import { msg } from "../i18n";

// A dropped player's seat is saved for REJOIN_MS (see room.js). Signed-in
// players are taken back to the game automatically; guests get it back by
// joining with the same name through the room link.

const sameName = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

// Does this game's state mention `uid` anywhere (scores, turn order, votes…)?
export const inGame = (state, uid) => !!state && !!uid && JSON.stringify(state).includes(uid);

// The saved guest seat this new guest can take over: same name, dropped less
// than REJOIN_MS ago, and still part of the game.
export function findSeat(players, state, me) {
  const mine = players[me];
  // The big screen (TV mode) isn't a player, so it never takes a seat.
  if (!mine?.isGuest || mine.screen || inGame(state, me)) return null;
  const now = serverNow();
  const seats = Object.entries(players)
    .filter(([id, p]) => id !== me && p.isGuest && sameName(p.name, mine.name) && recentlyLeft(p, now) && inGame(state, id))
    .sort((a, b) => (b[1].lastSeen || 0) - (a[1].lastSeen || 0));
  return seats[0]?.[0] || null;
}

// ---- Moving a seat to a new player id --------------------------------------
// Player ids are long random strings, so replacing them wherever they show up
// (as keys, values or inside strings) is safe.

const swap = (s, from, to) => s.split(from).join(to);

export function renameId(value, from, to) {
  if (typeof value === "string") return swap(value, from, to);
  if (Array.isArray(value)) return value.map((v) => (v === undefined ? null : renameId(v, from, to)));
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [swap(k, from, to), renameId(v, from, to)]));
  }
  return value;
}

// The smallest update that renames `from` to `to` under `path`, so writes other
// players make at the same moment (votes, answers…) aren't overwritten.
export function renamePatch(path, value, from, to, patch = {}) {
  if (value === null || value === undefined || !JSON.stringify(value).includes(from)) return patch;
  if (typeof value !== "object" || Array.isArray(value)) {
    patch[path] = renameId(value, from, to);
    return patch;
  }
  for (const [k, v] of Object.entries(value)) {
    if (k.includes(from)) {
      patch[`${path}/${k}`] = null;
      patch[`${path}/${swap(k, from, to)}`] = renameId(v, from, to);
    } else {
      renamePatch(`${path}/${k}`, v, from, to, patch);
    }
  }
  return patch;
}

const read = async (code, path) => {
  try {
    return (await get(roomRef(code, path))).val();
  } catch {
    return null;
  }
};

// Host only: hands the seat of `from` (a dropped guest) to `to` (the same
// person on a new device). `extra` adds game-specific moves, e.g. re-dealing
// private roles the host can't read.
export async function moveSeatPatch({ code, from, to, players, extra }) {
  const [state, answer, nightacts, chains] = await Promise.all(
    ["state", "answer", "nightacts", "chains"].map((p) => read(code, p))
  );
  const patch = {};
  renamePatch("state", state, from, to, patch);
  renamePatch("answer", answer, from, to, patch);
  renamePatch("nightacts", nightacts, from, to, patch);
  renamePatch("chains", chains, from, to, patch);
  patch[`players/${from}`] = null;
  // Keep their place in the join order (used for turn order and host hand-off).
  patch[`players/${to}/joinedAt`] = players[from].joinedAt;
  patch[`players/${to}/claim`] = null;
  if (extra) {
    Object.assign(
      patch,
      await extra({ code, from, to, read: (p) => read(code, p), state: renameId(state, from, to), answer: renameId(answer, from, to) })
    );
  }
  return patch;
}

// Host only: watches for guests claiming a saved seat and moves it to them.
export function useSeatClaims({ code, meta, players, isHost, joined, extra }) {
  const busy = useRef(new Set());
  const playing = meta?.status === "playing";
  useEffect(() => {
    if (!isHost || !joined || !playing) return;
    for (const [to, p] of Object.entries(players)) {
      const from = p.claim;
      if (!from || busy.current.has(to)) continue;
      busy.current.add(to);
      const seat = players[from];
      const valid = seat && from !== to && seat.isGuest && p.isGuest && sameName(seat.name, p.name) && recentlyLeft(seat);
      const done = valid
        ? moveSeatPatch({ code, from, to, players, extra }).then((patch) =>
            roomUpdate(code, patch, [systemMsg(msg("🔄 {name} is back! Their seat was saved.", { name: p.name }), { tone: "good" })])
          )
        : set(roomRef(code, `players/${to}/claim`), null);
      done
        .catch((e) => {
          console.error("Couldn't move seat", e);
          return set(roomRef(code, `players/${to}/claim`), null).catch(() => {});
        })
        .finally(() => busy.current.delete(to));
    }
  }, [code, players, isHost, joined, playing, extra]);
}

// ---- Signed-in players: remember the room ------------------------------------
const LAST_ROOM_KEY = "partycade.lastRoom";

export function rememberRoom(uid, code) {
  try {
    localStorage.setItem(LAST_ROOM_KEY, JSON.stringify({ uid, code }));
  } catch {}
}

export function forgetRoom() {
  try {
    localStorage.removeItem(LAST_ROOM_KEY);
  } catch {}
}

export function lastRoom(uid) {
  try {
    const r = JSON.parse(localStorage.getItem(LAST_ROOM_KEY));
    return r && r.uid === uid ? r.code : null;
  } catch {
    return null;
  }
}

// Is there a game still running in `code` that `uid` dropped out of less than
// REJOIN_MS ago? Returns "offline" (take them back), "online" (open in another
// tab or device, so just offer) or null.
export async function rejoinable(code, uid) {
  const [meta, me] = await Promise.all([read(code, "meta"), read(code, `players/${uid}`)]);
  if (meta?.status !== "playing" || !me) return null;
  if (me.online) return "online";
  return recentlyLeft(me) ? "offline" : null;
}

// Guest side: joined a running game under a new id with the same name as a
// dropped guest? Ask the host for that seat. `onBack` runs once it's ours,
// with the seat's old avatar.
export function useClaimSeat({ code, meta, players, state, me, joined, onBack }) {
  const claim = useRef(null);
  const playing = joined && meta?.status === "playing";
  const seat = useMemo(() => (playing && state ? findSeat(players, state, me) : null), [playing, players, state, me]);
  useEffect(() => {
    if (!seat || claim.current?.seat === seat) return;
    claim.current = { seat, avatar: players[seat]?.avatar };
    set(roomRef(code, `players/${me}/claim`), seat).catch(() => {});
  }, [code, me, seat, players]);
  const back = !!claim.current && inGame(state, me);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  useEffect(() => {
    if (!back) return;
    onBackRef.current(claim.current.avatar);
    claim.current = null;
  }, [back]);
}
